import {
    animate,
    attrEffect,
    easeInOut,
    mapValue,
    MotionValue,
    motionValue,
    scroll,
    styleEffect,
    transformValue,
    ValueAnimationTransition,
} from 'motion';

const itemsPositions = {
    desktop: [
        ['-38vw', '-20vw', '0vw', '0vw'],
        ['-24vw',  '-4vw', '0vw', '0vw'],
        [ '24vw',  '-4vw', '0vw', '0vw'],
        [ '38vw', '-20vw', '0vw', '0vw']
    ],
    tablet: [
        ['-111px', '-464px', '50px', '0px'],
        ['-111px', '-242px', '50px', '0px'],
        [ '111px', '-242px', '50px', '0px'],
        [ '111px', '-464px', '50px', '0px']
    ],
    mobile: [
        ['-82px', '-360px', '25px', '0px'],
        ['-82px', '-192px', '25px', '0px'],
        [ '82px', '-192px', '25px', '0px'],
        [ '82px', '-360px', '25px', '0px']
    ]
};

const itemsDelays = {
    desktop: [0, 0, 0, 0],
    tablet: [0.05, 0, 0.025, 0.075],
    mobile: [0.05, 0, 0.025, 0.075],
};

const springConfig: ValueAnimationTransition<number> = {
    type: 'spring',
    stiffness: 140,
    damping: 24,
    restDelta: 0.001,
};

export default class HeaderAnimation {
    static mobileMatcher = window.matchMedia('not (min-width: 48em)'); // < md
    static tabletMatcher = window.matchMedia('(48em <= width < 62em)'); // = md
    static desktopMatcher = window.matchMedia('(min-width: 62em)'); // >= lg

    static getViewport(): "mobile" | "tablet" | "desktop" {
        switch (true) {
            case this.mobileMatcher.matches: return "mobile";
            case this.tabletMatcher.matches: return "tablet";
            case this.desktopMatcher.matches: return "desktop";
            default: return "desktop";
        }
    }

    static init(): void {
        const scrollContainer = document.querySelector('.home-header__top')!;
        if (!scrollContainer) return;
        const backgroundCircle = document.querySelector('.home-header__circle-center-bg')!;
        const circleCenter = document.querySelector('.home-header__circle-center')!;
        const logo = document.querySelector('.home-header__circle-center-logo')!;
        const logoText = document.querySelector('.home-header__circle-center-logo-text')!;
        const logoTitle = document.querySelector('.home-header__circle-center-logo-title')!;
        const scrollIndicator = document.querySelector('.home-header__scroll-indicator')!;

        if (
            !backgroundCircle ||
            !circleCenter ||
            !logo ||
            !logoText ||
            !logoTitle ||
            !scrollIndicator
        ) {
            return;
        }
        
        setTimeout(() => this.initMouseParallax(), 0);
        this.initFixedAtEnd();

        const scrollProgress = motionValue(0);
        scroll(
            (progress: number) => {
                if (scrollProgress.isAnimating()) {
                    scrollProgress.stop();
                }
                animate(scrollProgress, progress, springConfig);
            },
            {
                target: scrollContainer,
                offset: ['start start', 'end start'],
            },
        );

        const itemsPositionsObjects: MotionValue[] = [];
        const setItemsPositions = () => {
            itemsPositionsObjects.forEach(object => object.destroy());

            const viewport = this.getViewport();
            document.querySelectorAll('.home-header__item').forEach((item, idx) => {
                const delay = itemsDelays[viewport][idx];
                const position = itemsPositions[viewport][idx];
                const scale = mapValue(scrollProgress, [0 + delay, 0.25 + delay], [1, 0], { ease: easeInOut });
                const x = mapValue(scrollProgress, [0 + delay, 0.25 + delay], [position[0], position[2]], { ease: easeInOut });
                const y = mapValue(scrollProgress, [0 + delay, 0.25 + delay], [position[1], position[3]], { ease: easeInOut });
                
                styleEffect(item, {
                    scale,
                    left: x,
                    top: y
                });

                itemsPositionsObjects.push(scale, x, y);
            });
        };
        this.tabletMatcher.addEventListener("change", setItemsPositions);
        setItemsPositions();

        const scrollIndicatorHidden = transformValue(() => {
            return scrollProgress.get() < 0.02 ? false : true;
        });
        attrEffect(scrollIndicator, {
            dataHidden: scrollIndicatorHidden,
        });

        const sizeCircle = mapValue(scrollProgress, [0.15, 0.35], ['100px', '272px']);
        styleEffect(circleCenter, {
            width: sizeCircle,
            height: sizeCircle,
        });

        const backgroundScale = mapValue(scrollProgress, [0.15, 0.75], [1, 20]);
        styleEffect(backgroundCircle, {
            scale: backgroundScale,
        });

        const positionLogo = mapValue(scrollProgress, [0.15, 0.35], ['38px', '108px']);
        styleEffect(logo, {
            top: positionLogo,
        });

        const opacityLogoText = mapValue(scrollProgress, [0.25, 0.35], [0, 1]);
        styleEffect(logoText, {
            opacity: opacityLogoText,
        });

        const opacityLogoTitle = mapValue(scrollProgress, [0.2, 0.35], [0, 1]);
        styleEffect(logoTitle, {
            opacity: opacityLogoTitle,
        });
    }

    static initMouseParallax(): void {
        if (!window.matchMedia("(hover: hover)")) return;
        
        const items = document.querySelectorAll('.home-header__item')!;
        if (!items.length) {
            return;
        }

        const mouseX = motionValue(0.5);
        const mouseY = motionValue(0.5);

        window.addEventListener('mousemove', (e: MouseEvent) => {
            const normalizedX = e.clientX / window.innerWidth;
            const normalizedY = e.clientY / window.innerHeight;

            mouseX.stop();
            mouseY.stop();

            animate(mouseX, normalizedX, springConfig);
            animate(mouseY, normalizedY, springConfig);
        });

        const itemParallaxX = mapValue(mouseX, [0, 1], [-4, 4]);
        const itemParallaxY = mapValue(mouseY, [0, 1], [-4, 4]);
        styleEffect(items, {
            x: itemParallaxX,
            y: itemParallaxY,
        });
    }

    static initFixedAtEnd(): void {
        const self = document.querySelector<HTMLElement>('.home-header');
        if (!self) return;
        const content = document.querySelector<HTMLElement>('.home-header__content');
        const container = document.querySelector<HTMLElement>('.home-header__circle-container');
        if (!content || !container) return;

        const containerStyle = getComputedStyle(container);
        let isHeaderFixed = false;

        const onChange = () => {
            const { bottom } = self.getBoundingClientRect();

            if (bottom <= window.innerHeight) {
                if (isHeaderFixed) return;

                const { height, top } = containerStyle;
                const circleBlockSize = parseFloat(height) + parseFloat(top);
                if (circleBlockSize > window.innerHeight) return;
               
                self.style.minHeight = content.offsetHeight + 'px';
                content.classList.add('is-fixed');
                isHeaderFixed = true;
            } else if (isHeaderFixed) {
                self.style.minHeight = '';
                content.classList.remove('is-fixed');
                isHeaderFixed = false;
            }
        };

        window.addEventListener('resize', onChange);
        window.addEventListener('scroll', onChange, { passive: false });
    }
}
